'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/ml-extra'

const SoftmaxTrainer = dynamic(() => import('../_components/widgets/MLExtraWidgets').then(m => m.SoftmaxTrainer), { ssr: false })

export default function Multiclass() {
  return (
    <Lesson>
      <Step>
        <H>More than yes or no</H>
        <P>
          Logistic regression answers yes-or-no questions. But which digit is this, 0 to 9? Which of 50 000 words comes next?
          For <M>K</M> classes we give the model <M>K</M> scores (one weighted sum per class) and turn them into probabilities
          with <strong>softmax</strong>, from Module 5.
        </P>
        <MB>{'z_k = \\mathbf{w}_k \\cdot \\mathbf{x} + b_k \\qquad \\mathbf{p} = \\text{softmax}(\\mathbf{z})'}</MB>
        <P>
          All the weights together form a matrix <M>W</M>: one row per input feature, one column per class. The loss is
          cross-entropy, and the gradient is just as tidy as before: predicted probabilities minus the true answer.
        </P>
        <MB>{'\\frac{\\partial L}{\\partial z_k} = p_k - [k = y]'}</MB>
        <Figure n="6.5" wide caption="Three classes, three scores per point. Press Train: each colour’s region is where its score wins. The borders between regions are always straight lines.">
          <SoftmaxTrainer />
        </Figure>
      </Step>

      <Step gate={['q-onehot', 'softmax-regression']}>
        <H>One-hot labels</H>
        <P>
          To compare the probabilities with the right answer, the answer is written as a <strong>one-hot</strong> vector: all
          zeros, with a 1 in the correct class’s position. Class 2 of 4 is <C>[0, 0, 1, 0]</C>.
        </P>
        <Quiz
          id="q-onehot"
          question={
            <>
              The model predicts <C>[0.1, 0.7, 0.2]</C> and the answer is class 1. What is <M>{'\\mathbf{p} - \\text{onehot}'}</M>?
            </>
          }
          options={[
            { label: '[0.1, −0.3, 0.2]', correct: true },
            { label: '[0.1, 0.7, 0.2]', why: 'Subtract the one-hot [0, 1, 0] first.' },
            { label: '[−0.9, 0.7, −0.8]', why: 'The one-hot is 1 only at the correct class (index 1).' },
          ]}
          explain="Wrong classes get pushed down by their probability; the right class gets pulled up by how much it was missing. That’s the whole learning signal."
        />
        <Exercise
          id="softmax-regression"
          title="oneHot, scores, train"
          {...EX.softmaxRegression}
          hints={[
            <>
              oneHot: <C>{'const v = new Array(k).fill(0); v[label] = 1; return v'}</C>
            </>,
            <>
              scores: start each class at <C>b[k]</C> and add <C>{'x[j] * W[j][k]'}</C> for every feature j.
            </>,
            <>
              In train, for each example and class: <C>{'const err = (p[c] - t[c]) / n'}</C>, add it to <C>gb[c]</C> and{' '}
              <C>{'err * X[i][j]'}</C> to <C>gW[j][c]</C>. After the loop over examples, step every weight.
            </>,
          ]}
        />
      </Step>

      <Step gate="digits-linear">
        <H>Reading handwriting</H>
        <P>
          Time for a real task: handwritten digits, 14 × 14 pixels each, so 196 inputs and 10 classes. It’s the same code as
          above, just wider.
        </P>
        <Exercise id="digits-linear" timeoutMs={60000} title="A linear digit reader" {...EX.digitsLinear} hints={[<>Count the test images where <C>{'vec.argmax(scores(model.W, model.b, x)) === testY[i]'}</C>, and divide by <C>testX.length</C>.</>]} />
        <Callout kind="why">
          Look at the weight picture: bright pixels are where ink means “3”, dark ones where ink means “not a 3”. A linear
          model can only learn one fixed template per class, so a slanted or squashed 3 that doesn’t match the template gets
          missed. Neural networks (Module 7) learn many templates and combine them, and get much closer to 100%.
        </Callout>
      </Step>
    </Lesson>
  )
}
